// OAuth full-page navigation must hit the API host DIRECTLY so Auth.js PKCE/state
// cookies are set and read on the same (backend) domain. Keep pointing at the API origin.
import { isFieldModeEnabled } from '../auth/FieldModeContext';

const configuredApiBase = process.env.REACT_APP_API_URL?.trim() || '';

/**
 * Data fetches (credentials: 'include') go SAME-ORIGIN so the session cookie is first-party.
 * Mobile Safari/Chrome block third-party cookies, which broke cross-site auth to the API host
 * (401 on mobile, fine on desktop). Production: Vercel rewrites `/api/*` and `/uploads/*` to the
 * backend (see sutra/vercel.json). Dev: CRA proxies `/api` to the backend (see package.json).
 */
const API_BASE: string = '';

/**
 * Backend origin for full-page OAuth navigation (must NOT go through the SPA host).
 * Auth.js + PKCE cookies live on the API host (localhost:4000 in dev, Render in prod).
 */
export function getApiOrigin(): string {
  if (configuredApiBase) return configuredApiBase.replace(/\/+$/, '');
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return `http://${host === '127.0.0.1' ? '127.0.0.1' : 'localhost'}:4000`;
    }
  }
  return typeof window !== 'undefined' ? window.location.origin : '';
}

/** Full URL that starts Google OAuth on the API server (307 → accounts.google.com). */
export function buildGoogleSignInUrl(): string {
  const origin = getApiOrigin();
  const bridge = `${origin}/api/auth/oauth-bridge`;
  return `${origin}/api/auth/google?callbackUrl=${encodeURIComponent(bridge)}`;
}

export function startGoogleSignIn(): void {
  window.location.assign(buildGoogleSignInUrl());
}

/** Legacy bearer storage — cleared on load; auth uses HttpOnly cookies only. */
const LEGACY_BEARER_KEY = 'tenant_session_bearer_v1';

if (typeof window !== 'undefined') {
  try {
    sessionStorage.removeItem(LEGACY_BEARER_KEY);
  } catch {
    // ignore
  }
}

type RequestOptions = RequestInit & { json?: unknown };

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    ...options,
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      ...(isFieldModeEnabled() ? { 'X-Field-Mode': '1' } : {}),
      ...(options.headers || {})
    },
    credentials: 'include'
  };

  if (options.json !== undefined) {
    init.body = JSON.stringify(options.json);
  }

  const url = `${API_BASE}${path}`;
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed';
    throw new Error(`Failed to reach API at ${url}. ${message}`);
  }

  const raw = await response.text();
  let data: any = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    data = {};
  }

  if (!response.ok) {
    const fallback = raw?.slice(0, 180) || 'Request failed';
    const message = (data && (data.error || data.message)) || fallback;
    throw new Error(`HTTP ${response.status} ${response.statusText}: ${message}`);
  }

  return data as T;
}

export function buildApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}${normalized}`;
  if (typeof window !== 'undefined') return `${window.location.origin}${normalized}`;
  return normalized;
}

/**
 * Full-page navigation on the SPA host (Vercel / CRA dev server) so the
 * workspace session cookie is sent. Unlike getApiOrigin(), this must NOT jump
 * to the API host — login cookies are first-party on the frontend origin.
 */
export function buildSameOriginApiUrl(path: string): string {
  return buildApiUrl(path);
}

export async function downloadPdfFromApi(params: {
  route: string;
  query: Record<string, string>;
  fileName: string;
}): Promise<void> {
  const qs = new URLSearchParams(params.query).toString();
  const url = `${API_BASE}${params.route}?${qs}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: isFieldModeEnabled() ? { 'X-Field-Mode': '1' } : undefined
  });

  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    throw new Error(`PDF download failed (HTTP ${response.status}): ${raw || response.statusText}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = params.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
}

function mimeForFileName(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return 'application/octet-stream';
}

function apiPathFromUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return new URL(pathOrUrl).pathname;
  }
  return pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
}

async function fetchAuthenticatedBlob(pathOrUrl: string): Promise<Blob> {
  const path = apiPathFromUrl(pathOrUrl);
  const url = buildApiUrl(path);
  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
    headers: isFieldModeEnabled() ? { 'X-Field-Mode': '1' } : undefined
  });

  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    let message = raw?.slice(0, 200) || response.statusText || 'Failed to download file';
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && parsed !== null && 'error' in parsed) {
        const err = (parsed as { error?: unknown }).error;
        if (typeof err === 'string' && err.trim()) message = err.trim();
      }
    } catch {
      /* use raw slice */
    }
    throw new Error(`HTTP ${response.status}: ${message}`);
  }

  return response.blob();
}

/** Open a protected upload in a new tab (PDF) or trigger a download. */
export async function openApiFile(pathOrUrl: string, fileName: string): Promise<void> {
  const blob = await fetchAuthenticatedBlob(pathOrUrl);
  const mime = blob.type || mimeForFileName(fileName);
  const typedBlob = blob.type ? blob : new Blob([blob], { type: mime });
  const objectUrl = URL.createObjectURL(typedBlob);
  const isPdf = mime === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');

  try {
    if (isPdf) {
      const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        throw new Error('Pop-up blocked. Allow pop-ups for this site to view PDFs.');
      }
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
}

export async function downloadApiFile(path: string, fileName: string): Promise<void> {
  await openApiFile(path, fileName);
}
